import { FileHeader, HeaderMapping, PortfolioPropertiesMap, PropertyConfig } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, NonCancelableCustomEvent, Select, SelectProps } from '@amzn/awsui-components-react';
import React, { Component } from 'react';

import { constants } from '../../../../common/Localization';
import ShrinkHotspot from '../../../common/ShrinkHotspot';
import { HotspotIds } from '../../../tutorials/HotspotIds';
import Table from '../common-components/NativeHtmlTable';
import { SHOW_MAPPED_OPTIONS } from '../common-util/resources';
import {
    OnChangeAsync,
    OnChangeSync,
    SHOW_MAPPED_STATUS,
} from '../common-util/types';
import { WizardContentProps } from '../WizardContentProps';
import {
    EmptyState,
    FileHeaderSelectColCell,
    MandatoryPropertyColCell,
    MeasurementColCell,
    OverwriteColCell,
} from './HeaderMappingColumnDefinitions';

interface Props extends WizardContentProps {
    fileHeaders: FileHeader[];
    portfolioProperties: PortfolioPropertiesMap;
    mandatoryProperties: PropertyConfig[];
    onMandatoryHeaderMappingChange: OnChangeAsync;
    onUomChange: OnChangeSync;
    onOpenOverwriteModal: OnChangeSync;
    onSwitchOverwriteToggle: OnChangeSync;
}

interface State {
    items: HeaderMapping[];
    showMappedOption: SelectProps.Option;
    infoAlertVisible: boolean;
}

class MandatoryPropertiesTable extends Component<Props, State> {
    columnDefinitions = [
        {
            cell: (item: HeaderMapping) => MandatoryPropertyColCell(item),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.EXPECTED_SCHEMA,
            id: 'portfolioProperty',
        },
        {
            cell: (item: HeaderMapping) => FileHeaderSelectColCell(item, this.props.fileHeaders, this.props.onMandatoryHeaderMappingChange),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.FILE_HEADER,
            id: 'fileHeader',
        },
        {
            cell: (item: HeaderMapping) => MeasurementColCell(item, this.props.onUomChange),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.MEASUREMENT,
            id: 'UOM',
        },
        {
            cell: (item: HeaderMapping) => OverwriteColCell(item, this.props.onOpenOverwriteModal, this.props.onSwitchOverwriteToggle),
            header: constants.HEADER_MAPPING_TABLE_HEADERS.OVERWRITE_RECORDS,
            id: 'overwrite',
        },
    ];
    showOverwriteCountColumn = false;
    showUOMColumn = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            items: [],
            showMappedOption: SHOW_MAPPED_OPTIONS[0],
            infoAlertVisible: true,
        };
    }

    componentDidMount() {
        this.generateTableItems();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps.currentStep !== this.props.currentStep || prevProps.mandatoryProperties !== this.props.mandatoryProperties
            || prevProps.fileHeaders !== this.props.fileHeaders || prevProps.portfolioProperties !== this.props.portfolioProperties
            || prevProps.propertiesConfig !== this.props.propertiesConfig || this.state.showMappedOption !== prevState.showMappedOption) {
            this.generateTableItems();
        }
    }

    render() {
        const tableTitle = (
            <div className='awsui-util-action-stripe'>
                <div className='awsui-util-action-stripe-title'>
                    <ShrinkHotspot hotspotId={HotspotIds.RECOMMENDED_HEADER_MAPPING}>
                        <h2>
                            {constants.MANDATORY_HEADERS_TITLE}
                            <span className='awsui-util-header-counter'> ({this.state.items.length})</span>
                        </h2>
                    </ShrinkHotspot>

                </div>
                <div className='awsui-util-action-stripe-group'>
                    <Select options={SHOW_MAPPED_OPTIONS} selectedOption={this.state.showMappedOption} onChange={this.onShowOptionChange}/>
                </div>
            </div>
        );
        const columnDefinitions = this.columnDefinitions.filter(columnDefinition =>
            !(columnDefinition.id === 'overwrite' && !this.showOverwriteCountColumn) &&
            !(columnDefinition.id === 'UOM' && !this.showUOMColumn),
        );
        const infoAlert = this.props.wizardContext.type === 'server' && this.state.infoAlertVisible ? (
            <Alert
                visible={this.state.infoAlertVisible}
                onDismiss={() => this.setState({infoAlertVisible: false})}
                dismissible
                header={constants.MANDATORY_HEADERS_TOOLTIPS.HEADER}
                className='awsui-util-pb-m'
            >
                <ul>
                    <li>{constants.MANDATORY_HEADERS_TOOLTIPS.HIGHLY_RECOMMENDED}</li>
                    <li>{constants.MANDATORY_HEADERS_TOOLTIPS.PHYSICAL_VIRTUAL}</li>
                    <li>{constants.MANDATORY_HEADERS_TOOLTIPS.CPU}</li>
                    <li>{constants.MANDATORY_HEADERS_TOOLTIPS.UOM}</li>
                </ul>
            </Alert>
        ) : null;
        return (
            <div className='awsui-util-pt-l'>
                {infoAlert}
                <Table
                    columnDefinitions={columnDefinitions}
                    items={this.state.items}
                    tableTitle={tableTitle}
                    emptyState={EmptyState}
                    tableId='mandatoryHeaderMapping'
                />
            </div>
        );
    }

    onShowOptionChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        this.setState({
            showMappedOption: event.detail.selectedOption,
        });
    };

    generateTableItems(): void {
        let overwriteCountSum = 0;
        let showUOMColumn = false;
        const items = this.props.mandatoryProperties.filter((mandatoryProperty: PropertyConfig) => {
            const portfolioProperty = this.props.portfolioProperties[mandatoryProperty.value];
            // skip unmapped properties when showing mapped properties only
            if (this.state.showMappedOption.value === SHOW_MAPPED_STATUS.MAPPED && !portfolioProperty.fileHeaderIndex) {
                return false;
            }
            // skip mapped properties when showing unmapped properties only
            if (this.state.showMappedOption.value === SHOW_MAPPED_STATUS.UNMAPPED && portfolioProperty.fileHeaderIndex) {
                return false;
            }
            return true;
        }).map((mandatoryProperty: PropertyConfig) => {
            const portfolioProperty = this.props.portfolioProperties[mandatoryProperty.value];
            const fileHeaderIndex = portfolioProperty?.fileHeaderIndex || 0;
            const targetFileHeader = this.props.fileHeaders[fileHeaderIndex];
            const uniqueValues = targetFileHeader?.uniqueValues;
            const overwriteCount = portfolioProperty?.overwriteCount || 0;
            overwriteCountSum += overwriteCount;
            showUOMColumn = showUOMColumn || !!mandatoryProperty.UOM;
            return {
                UOM: mandatoryProperty.UOM,
                dbUOM: mandatoryProperty.dbUOM,
                fileHeader: targetFileHeader?.label,
                fileHeaderIndex,
                fileIdToValuesMap: portfolioProperty?.fileIdToValuesMap || {},
                fileUOM: portfolioProperty?.fileUOM,
                idToValueMap: portfolioProperty?.idToValueMap || {},
                isId: mandatoryProperty.isId,
                overwrite: portfolioProperty?.overwrite,
                overwriteCount,
                portfolioProperty: mandatoryProperty.value,
                portfolioPropertyLabel: mandatoryProperty.text,
                uniqueValues,
            } as HeaderMapping;
        });
        this.showOverwriteCountColumn = overwriteCountSum !== 0;
        this.showUOMColumn = showUOMColumn;
        this.setState({
            items,
        });
    }
}

export default MandatoryPropertiesTable;
